import { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, ClipboardList, Clock, AlertTriangle, CheckCircle2, Mail, Archive, ArchiveRestore } from "lucide-react";
import { HEALTH_DOT, actionHealth, originOf } from "@/lib/health";
import { DotLegend } from "@/components/ReportExtras";
import ParkFields, { validateParked, ParkedBadge } from "@/components/ParkFields";

const BUCKETS = {
  late: { label: "Late", color: "#EF4444", icon: AlertTriangle },
  pending: { label: "Pending", color: "#10B981", icon: Clock },
  completed: { label: "Completed", color: "#2563EB", icon: CheckCircle2 },
};
const DONUT_COLORS = { late: "#EF4444", pending: "#10B981", completed: "#2563EB" };
const STATUS = [
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "parked", label: "On Hold / Parked" },
  { key: "done", label: "Done" },
];
const blank = { title: "", description: "", owner: "", due_date: "", status: "open", priority: "medium", source: "general", linked_task_id: "", outcome: "", parked_reason: "", parked_revisit: "" };

export default function ActionItems({ projectId, tasks = [] }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingFu, setSendingFu] = useState(false);
  const [names, setNames] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/resource-registry").then((r) => r.data).catch(() => []),
      api.get(`/projects/${projectId}/resources`).then((r) => r.data).catch(() => []),
    ]).then(([g, p]) => {
      const set = new Set([...g, ...p].map((x) => (x.name || "").trim()).filter(Boolean));
      setNames([...set].sort((a, b) => a.localeCompare(b)));
    });
  }, [projectId]);

  const sendFollowUps = async () => {
    setSendingFu(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/action-items/follow-up`);
      if (data.sent) {
        toast.success(`Follow-up emails sent to ${data.sent} owner(s)` +
          (data.unresolved?.length ? ` — no email found for: ${data.unresolved.join(", ")}` : ""), { duration: 8000 });
      } else if (!data.email_configured) {
        toast.error("Email isn't configured yet (missing Resend key)");
      } else {
        toast.error(data.unresolved?.length ? `No email address found for: ${data.unresolved.join(", ")} — add them to the resource registry or use an email as the owner` : (data.error || "Could not send follow-ups"), { duration: 10000 });
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send follow-ups");
    } finally { setSendingFu(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/action-items?include_archived=1`);
      setItems(data);
    } catch (e) { setItems([]); }
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(blank); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...blank, ...a, due_date: a.due_date || "", linked_task_id: a.linked_task_id || "" }); setEditId(a.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!validateParked(form, toast.error)) return;
    setSaving(true);
    try {
      const payload = { ...form, due_date: form.due_date || null, linked_task_id: form.linked_task_id || null, parked_revisit: form.parked_revisit || null };
      if (editId) await api.put(`/action-items/${editId}`, payload);
      else await api.post(`/projects/${projectId}/action-items`, payload);
      setOpen(false);
      toast.success(editId ? "Updated" : "Added");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/action-items/${id}`);
    setOpen(false);
    toast.success("Deleted");
    load();
  };

  const grouped = useMemo(() => {
    const g = { late: [], pending: [], completed: [] };
    (items || []).filter((a) => !a.archived).forEach((a) => g[a.bucket || "pending"].push(a));
    return g;
  }, [items]);
  const active = useMemo(() => (items || []).filter((a) => !a.archived), [items]);
  const archivedItems = useMemo(() => (items || []).filter((a) => a.archived), [items]);
  const [showArchived, setShowArchived] = useState(false);

  const setArchived = async (id, archived) => {
    try {
      await api.patch(`/action-items/${id}/archive`, { archived });
      toast.success(archived ? "Action item archived" : "Action item restored");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update"); }
  };

  const archiveAllCompleted = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/action-items/archive-completed`);
      toast.success(`Archived ${data.archived} completed item${data.archived === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error("Could not archive"); }
  };

  const chartData = useMemo(() =>
    Object.entries(BUCKETS).map(([k, b]) => ({ name: b.label, value: grouped[k].length, color: DONUT_COLORS[k] })).filter((d) => d.value > 0),
  [grouped]);

  if (items === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="action-items-view">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold">Action items <span className="text-sm text-muted-foreground font-normal">({active.length})</span></h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={sendFollowUps} disabled={sendingFu || !items.some((a) => a.status !== "done")} title="Email each owner their open action items with a one-click check-off link" data-testid="action-followup-button">
            {sendingFu ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email follow-ups
          </Button>
          <Button className="gap-2" onClick={openNew} data-testid="add-action-item-button"><Plus className="h-4 w-4" /> New action item</Button>
        </div>
      </div>

      {/* KPI + chart */}
      <div className="grid md:grid-cols-4 gap-4">
        {Object.entries(BUCKETS).map(([k, b]) => (
          <div key={k} className="border border-border rounded-lg p-5" data-testid={`ai-kpi-${k}`}>
            <div className="flex items-center justify-between">
              <p className="overline text-muted-foreground">{b.label}</p>
              <b.icon className="h-4 w-4" style={{ color: b.color }} />
            </div>
            <p className="font-display text-3xl font-extrabold tracking-tight mt-2" style={{ color: b.color }}>{grouped[k].length}</p>
          </div>
        ))}
        <div className="border border-border rounded-lg p-3 flex flex-col overflow-hidden" data-testid="ai-donut-card">
          <p className="overline text-muted-foreground" data-testid="ai-donut-title">Action Items Progress</p>
          {chartData.length === 0 ? <p className="text-xs text-muted-foreground my-auto text-center">No action items yet.</p> : (
            <ResponsiveContainer width="100%" height={92}>
              <PieChart margin={{ top: 2, right: 4, bottom: 2, left: 4 }}>
                <Pie data={chartData} dataKey="value" nameKey="name" cx="38%" cy="50%" innerRadius={22} outerRadius={38} paddingAngle={2} strokeWidth={0}>
                  {chartData.map((d, i) => <Cell key={d.name ?? i} fill={d.color} />)}
                </Pie>
                <text x="38%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 13, fontWeight: 800, fill: "#2563EB" }} data-testid="ai-donut-pct">
                  {active.length ? Math.round((grouped.completed.length / active.length) * 100) : 0}%
                </text>
                <Tooltip />
                <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 10, lineHeight: "16px" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {active.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center" data-testid="action-items-empty">
          <ClipboardList className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Capture action items here. They'll flow into Meeting prep and Reports.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <DotLegend />
          {["late", "pending", "completed"].map((k) => {
            const b = BUCKETS[k];
            if (!grouped[k].length) return null;
            return (
              <div key={k} data-testid={`ai-group-${k}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-bold uppercase tracking-wide flex items-center gap-1.5" style={{ color: b.color }}><b.icon className="h-3.5 w-3.5" /> {b.label} ({grouped[k].length})</p>
                  {k === "completed" && (
                    <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs text-muted-foreground" onClick={archiveAllCompleted} title="Move all completed items to the archive" data-testid="archive-all-completed">
                      <Archive className="h-3.5 w-3.5" /> Archive all
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {grouped[k].map((a) => (
                    <button key={a.id} onClick={() => openEdit(a)} className="w-full border border-border rounded-lg p-4 text-left hover:border-foreground/30 transition-colors" data-testid={`action-item-${a.id}`}>
                      <div className="flex items-start gap-3">
                        <span className="h-2.5 w-2.5 rounded-full mt-1.5 shrink-0" style={{ background: HEALTH_DOT[actionHealth(a)] }} title={actionHealth(a)} data-testid={`action-health-${a.id}`} />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium ${a.status === "done" ? "line-through text-muted-foreground" : ""}`}>
                            {a.display_id && <span className="font-mono text-[10px] font-bold text-primary mr-1.5" data-testid={`action-id-${a.id}`}>{a.display_id}</span>}
                            {a.title}
                          </p>
                          <div className="flex items-center flex-wrap gap-2 mt-1 text-[11px] text-muted-foreground">
                            {a.owner && <span>{a.owner}</span>}
                            {a.due_date && <span>· due {a.due_date}</span>}
                            <span>· {STATUS.find((s) => s.key === a.status)?.label}</span>
                            <ParkedBadge item={a} testid={`action-parked-${a.id}`} />
                            {a.source === "meeting" && <span className="px-1.5 rounded bg-primary/10 text-primary font-semibold" data-testid={`action-origin-${a.id}`}>{originOf(a) ? `meeting: ${originOf(a)}` : "meeting"}</span>}
                          </div>
                          {a.outcome && <p className="text-xs text-muted-foreground mt-1 italic">Outcome: {a.outcome}</p>}
                        </div>
                        {a.status === "done" && (
                          <span
                            role="button" tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); setArchived(a.id, true); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setArchived(a.id, true); } }}
                            className="shrink-0 p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-secondary"
                            title="Archive this completed item"
                            data-testid={`archive-action-${a.id}`}
                          ><Archive className="h-4 w-4" /></span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {archivedItems.length > 0 && (
        <div className="border-t border-border pt-4" data-testid="archived-actions-block">
          <button type="button" className="text-xs font-bold uppercase tracking-wide text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived-actions">
            <Archive className="h-3.5 w-3.5" /> Archived ({archivedItems.length}) {showArchived ? "▾" : "▸"}
          </button>
          {showArchived && (
            <div className="space-y-2 mt-3">
              {archivedItems.map((a) => (
                <div key={a.id} className="flex items-center gap-3 border border-border/60 rounded-lg p-3 bg-secondary/20 text-muted-foreground" data-testid={`archived-action-${a.id}`}>
                  {a.display_id && <span className="font-mono text-[10px] font-bold shrink-0">{a.display_id}</span>}
                  <span className="text-sm line-through truncate flex-1">{a.title}</span>
                  {a.outcome && <span className="text-xs italic truncate max-w-[200px]">Outcome: {a.outcome}</span>}
                  <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs shrink-0" onClick={() => setArchived(a.id, false)} data-testid={`restore-action-${a.id}`}>
                    <ArchiveRestore className="h-3.5 w-3.5" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="action-item-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Edit" : "New"} action item</DialogTitle>
            <DialogDescription>Track who owns it, when it's due, and the outcome.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label htmlFor="a-title">Title</Label>
              <Input id="a-title" required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="action-title-input" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Owner</Label>
                <Select value={form.owner || "none"} onValueChange={(v) => set("owner", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="action-owner-select"><SelectValue placeholder="Who owns this?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {form.owner && !names.includes(form.owner) && <SelectItem value={form.owner}>{form.owner}</SelectItem>}
                    {names.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
                {names.length === 0 && <p className="text-[11px] text-muted-foreground mt-1">Add people in Resources to populate this list.</p>}
              </div>
              <div>
                <Label htmlFor="a-due">Due date</Label>
                <Input id="a-due" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="action-due-input" className="mt-1.5" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="action-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="action-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <ParkFields form={form} set={set} testPrefix="action" />
            <div>
              <Label>Linked task (optional)</Label>
              <Select value={form.linked_task_id || "none"} onValueChange={(v) => set("linked_task_id", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="action-linked-task-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="a-desc">Description</Label>
              <Textarea id="a-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="action-desc-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="a-outcome">Outcome / notes</Label>
              <Textarea id="a-outcome" rows={2} value={form.outcome} onChange={(e) => set("outcome", e.target.value)} placeholder="Result once resolved" data-testid="action-outcome-input" className="mt-1.5" />
            </div>
            <DialogFooter className="flex sm:justify-between gap-2">
              {editId && (
                <Button type="button" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-2" onClick={() => remove(editId)} data-testid="action-delete-button">
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              )}
              <Button type="submit" disabled={saving} data-testid="action-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
